import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AlertComponent,
  ApolloDropdownComponent,
  ComboboxComponent,
  ComboboxConfig,
  DatepickerComponent,
  DropdownConfig,
  DropdownRowItem,
  InputComponent,
  ListItem,
  SelectListComponent,
  SelectListConfig,
  SelectListType,
} from '@degreed/apollo';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';
import { Attribute, AttributeDataType, Trigger } from '../../model';
import { AutomationsApiService } from '../../services/automations-api.service';
import { restoreRequiredSelection, synthesizeSelectedItem } from '../../utils';
import { NUMERIC_DATE_OPERATORS } from '../condition-form/condition-form/condition-form.component';
import { TriggerBackCancelSaveButtonsComponent } from '../trigger-back-cancel-save-buttons/trigger-back-cancel-save-buttons.component';

@Component({
  selector: 'dgx-trigger-attribute-changed-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    ApolloDropdownComponent,
    ComboboxComponent,
    DatepickerComponent,
    FormsModule,
    InputComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SelectListComponent,
    TriggerBackCancelSaveButtonsComponent,
  ],
  templateUrl: './trigger-attribute-changed-form.component.html',
})
export class TriggerAttributeChangedFormComponent implements OnInit {
  // Inputs
  public readonly trigger = input.required<Trigger>();
  public readonly orgId = input.required<number>();
  public readonly isSaving = input(false);
  public readonly scopedOrganizationalUnitId = input<number | undefined>(
    undefined
  );

  // Outputs
  public readonly cancelEmitter = output<void>();
  public readonly saveEmitter = output<Trigger>();
  public readonly backEmitter = output<void>();

  @ViewChild('attributesField', { read: ElementRef })
  public attributesField: ElementRef<HTMLElement>;
  @ViewChildren('inputElement')
  public inputElements: QueryList<InputComponent>;
  @ViewChildren('datePicker')
  public datePickers: QueryList<DatepickerComponent>;
  @ViewChildren('selectComponent', { read: ElementRef })
  public selectComponents: QueryList<ElementRef<HTMLElement>>;

  private readonly translate = inject(TranslateService);
  private readonly automationsService = inject(AutomationsApiService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly isInvalid = signal(false);
  public i18n = this.translate.instant([
    'Automation_AnyValueDisplay',
    'Automation_EndingAttributeValue',
    'Automation_MatchAnyEndingValue',
    'Automation_MatchAnyStartingValue',
    'Automation_StartingAttributeValue',
    'Automation_UserAttribute',
    'BusinessRules_AttributeName',
  ]);
  public readonly i18nShared = this.translate.instant([
    'Core_NoResults',
    'Core_FieldRequired',
    'Core_InvalidValue',
    'Core_InvalidEmail',
    'Core_Back',
    'Core_Save',
    'Core_Cancel',
    'Core_Search',
    'Core_Loading',
  ]);

  /**
   * Maps form-control error keys to user-facing messages. Used by the inline
   * `<p role="alert">` displays that surface errors for the controls Apollo
   * doesn't (yet) render errors for in a way that matches our submit-time
   * validation pattern. Mirrors da-input's built-in errorMessages map so
   * the wording stays consistent across the form.
   */
  private readonly errorMessages: Record<string, string> = {
    required: this.i18nShared.Core_FieldRequired,
    pattern: this.i18nShared.Core_InvalidValue,
    email: this.i18nShared.Core_InvalidEmail,
  };

  /**
   * Picks the first available error message for a control. Respects an
   * explicit `{ message: '...' }` payload, then falls back to our message
   * map, then to the raw error key so unknown validators still surface.
   */
  public errorMessageFor(
    errors: Record<string, unknown> | null | undefined
  ): string {
    if (!errors) return '';
    const [key, value] = Object.entries(errors)[0] ?? [];
    if (!key) return '';
    const explicit = (value as { message?: string })?.message;
    return explicit ?? this.errorMessages[key] ?? key;
  }
  public readonly attributes = signal<Attribute[]>([]);
  public attributeOptions: ListItem[] = [];
  public readonly triggerForm = signal<FormGroup | null>(null);
  public readonly AttributeDataType = AttributeDataType;
  public readonly numericDateOperators = NUMERIC_DATE_OPERATORS;
  public readonly booleanOptions: DropdownRowItem[] = [
    { id: 'True', name: 'True' },
    { id: 'False', name: 'False' },
  ];

  public attributeDropdownConfig: DropdownConfig = {
    id: 'attributeName-dropdown',
    label: this.i18n.BusinessRules_AttributeName,
    required: true,
  };

  public attributeComboboxConfig: ComboboxConfig = {
    id: 'attributeName',
    label: this.i18n.BusinessRules_AttributeName,
    options: [],
    selectListType: SelectListType.Single,
  };

  public readonly booleanSelectListConfig: SelectListConfig = {
    id: 'attribute-boolean-list',
    options: this.booleanOptions as unknown as Record<string, unknown>[],
    ariaLabel: 'True or False',
  };
  private readonly booleanDropdownConfigCache = new Map<
    string,
    DropdownConfig
  >();
  public getBooleanDropdownConfig(id: string, label: string): DropdownConfig {
    let config = this.booleanDropdownConfigCache.get(id);
    if (!config) {
      config = {
        id: `attribute-boolean-${id}`,
        label,
        required: true,
      };
      this.booleanDropdownConfigCache.set(id, config);
    }
    return config;
  }

  public get attributeControl() {
    return this.triggerForm()?.get('attribute') as FormControl;
  }
  public get startControl() {
    return this.triggerForm()?.get('start') as FormControl;
  }
  public get endControl() {
    return this.triggerForm()?.get('end') as FormControl;
  }
  public get isAnyStartControl() {
    return this.triggerForm()?.get('isAnyStart') as FormControl<boolean>;
  }
  public get isAnyEndControl() {
    return this.triggerForm()?.get('isAnyEnd') as FormControl<boolean>;
  }

  /**
   * Reactive snapshot of the FormGroup's value, populated once the form is
   * built in ngOnInit and kept in sync via a `valueChanges` subscription.
   */
  private readonly formValue = signal<Record<string, unknown> | null>(null);

  /** True when either wildcard checkbox is checked — drives the info banner. */
  public readonly showInfoBanner = computed(() => {
    const value = this.formValue();
    return !!value?.['isAnyStart'] || !!value?.['isAnyEnd'];
  });

  /**
   * Composed info-banner sentence. Picks one of three full-sentence keys
   * based on which side(s) are wildcarded — the wildcarded side has no
   * substituted variable, since word order and grammar around "any" vary
   * across languages and can't survive inline string substitution.
   */
  public readonly infoBannerText = computed(() => {
    const value = this.formValue();
    if (!value) return '';
    const anyStart = !!value['isAnyStart'];
    const anyEnd = !!value['isAnyEnd'];
    if (!anyStart && !anyEnd) return '';
    const attribute = (value['attribute'] as string) || '';
    if (anyStart && anyEnd) {
      return this.translate.instant(
        'Automation_AttributeChanged_InfoBanner_BothAny',
        { attribute }
      );
    }
    if (anyStart) {
      return this.translate.instant(
        'Automation_AttributeChanged_InfoBanner_StartAny',
        { attribute, endValue: (value['end'] as string) ?? '' }
      );
    }
    return this.translate.instant(
      'Automation_AttributeChanged_InfoBanner_EndAny',
      { attribute, startValue: (value['start'] as string) ?? '' }
    );
  });

  /**
   * Static, read-only control that backs the "Any value" placeholder input.
   * Kept as a FormControl (not a plain `[value]`) so da-input's CVA path
   * populates the visible input reliably — `value` on InputComponent is a
   * public property, not an `@Input`, so a template `[value]` binding is
   * unreliable under OnPush.
   */
  public readonly anyValueDisplayControl = new FormControl<string>({
    value: this.i18n.Automation_AnyValueDisplay,
    disabled: false,
  });

  public async ngOnInit(): Promise<void> {
    const result = await firstValueFrom(
      this.automationsService.getAttributes(
        this.orgId(),
        'BusinessRule',
        this.scopedOrganizationalUnitId()
      )
    );
    this.attributes.set(result.payload);

    this.attributeOptions = this.attributes()
      .filter((a) => a.attributeDataType !== AttributeDataType.StrArray)
      .map<ListItem>((c) => ({
        id: c.attributeName,
        name: c.attributeName,
      }));
    // Rebuild the config now that options are loaded; combobox reads the
    // input value on each CD pass so the new options will flow through.
    this.attributeComboboxConfig = {
      ...this.attributeComboboxConfig,
      options: this.attributeOptions,
    };

    const form = this.buildTriggerForm(this.trigger());
    this.triggerForm.set(form);
    this.formValue.set(form.value as Record<string, unknown>);
    form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.formValue.set(v as Record<string, unknown>));
    this.wireWildcardSubscriptions(form);
  }

  /** Build the wildcard-aware FormGroup from a persisted trigger. */
  private buildTriggerForm(triggerVal: Trigger): FormGroup {
    const isAnyStart = !!triggerVal.parameters?.IsAnyFromValue;
    const isAnyEnd = !!triggerVal.parameters?.IsAnyToValue;
    return new FormGroup({
      attribute: new FormControl(triggerVal.parameters?.AttributeName ?? '', [
        Validators.required,
      ]),
      start: new FormControl(
        isAnyStart ? '' : (triggerVal.parameters?.FromValue ?? ''),
        isAnyStart ? [] : [Validators.required]
      ),
      end: new FormControl(
        isAnyEnd ? '' : (triggerVal.parameters?.ToValue ?? ''),
        isAnyEnd ? [] : [Validators.required]
      ),
      isAnyStart: new FormControl<boolean>(isAnyStart, { nonNullable: true }),
      isAnyEnd: new FormControl<boolean>(isAnyEnd, { nonNullable: true }),
    });
  }

  /**
   * Wire the wildcard checkboxes: each toggles required-validation on its
   * paired value control, scoped to a single side.
   */
  private wireWildcardSubscriptions(form: FormGroup): void {
    const anyStart = form.get('isAnyStart');
    const anyEnd = form.get('isAnyEnd');
    if (!anyStart || !anyEnd) return;
    anyStart.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((checked) => this.applyWildcardToggle('start', !!checked));
    anyEnd.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((checked) => this.applyWildcardToggle('end', !!checked));
  }

  /**
   * Toggle a value control's required-validation and clear it when the
   * matching wildcard checkbox is checked. Scoped to a single side so
   * flipping the start checkbox never disturbs end validation (and vice
   * versa).
   */
  private applyWildcardToggle(which: 'start' | 'end', checked: boolean): void {
    const control = which === 'start' ? this.startControl : this.endControl;
    if (!control) return;
    if (checked) {
      control.clearValidators();
      control.setValue('', { emitEvent: false });
      control.markAsUntouched();
      // Clear the parallel datepicker display control so a stale date
      // doesn't linger behind the read-only placeholder on re-toggle.
      this.dateDisplayControls.get(which)?.setValue(null, { emitEvent: false });
    } else {
      control.setValidators([Validators.required]);
      control.markAsUntouched();
    }
    control.updateValueAndValidity();
  }

  /** Apollo combobox emits ListItem[] (single-select arrays of length 1). */
  public onAttributeSelect(
    items: ListItem[],
    dropdown: ApolloDropdownComponent,
    combobox: ComboboxComponent
  ) {
    const selected = items?.[items.length - 1];
    if (!selected) {
      restoreRequiredSelection(
        dropdown,
        combobox,
        this.attributeSelectionValues()
      );
      return;
    }
    if (selected.id === this.attributeControl?.value) return;
    this.attributeControl.setValue(String(selected.id));
    this.startControl.setValue('');
    this.endControl.setValue('');
  }

  /**
   * Memoized ListItem[] for the combobox's [selectionValues] binding. Stable
   * reference across CD passes prevents Apollo's overlay-base effect from
   * re-firing every cycle (same loop we saw on academy form).
   */
  private attributeSelectionCache: {
    value: unknown;
    items: ListItem[];
  } | null = null;
  public attributeSelectionValues(): ListItem[] {
    const value = this.attributeControl?.value;
    if (this.attributeSelectionCache?.value === value) {
      return this.attributeSelectionCache.items;
    }
    const match = this.attributeOptions.find((o) => o.id === value);
    const items = synthesizeSelectedItem(match, value);
    this.attributeSelectionCache = { value, items };
    return items;
  }

  public setBooleanValue(
    items: ListItem[],
    isStartingValue: boolean,
    dropdown: ApolloDropdownComponent,
    selectList: SelectListComponent
  ) {
    const option = items?.[0] as DropdownRowItem | undefined;
    if (!option) {
      const currentValue = isStartingValue
        ? this.startControl?.value
        : this.endControl?.value;
      restoreRequiredSelection(
        dropdown,
        selectList,
        this.getBooleanSelectedItems(currentValue)
      );
      return;
    }
    const value = String(option.id);
    if (isStartingValue) this.startControl.setValue(value);
    else this.endControl.setValue(value);
  }

  /**
   * Memoized ListItem[] for the boolean dropdown's [value] binding, keyed by
   * the saved 'True'/'False' string. Stable references across CD passes.
   */
  private readonly booleanItemsCache = new Map<string, ListItem[]>();
  public getBooleanSelectedItems(value: string): ListItem[] {
    const key = value ?? '';
    let items = this.booleanItemsCache.get(key);
    if (!items) {
      const match = this.booleanOptions.find((o) => o.id === key);
      items = match ? [match as ListItem] : [];
      this.booleanItemsCache.set(key, items);
    }
    return items;
  }

  /**
   * Parallel FormControl<DateTime> per start/end so da-datepicker can hydrate
   * its display via the CVA path. Apollo's datepicker doesn't update its
   * visible input from the `[value]` signal input — only its internal
   * calendar state — so a one-way binding renders an empty field on edit.
   * We keep this control bidirectionally in sync with the start/end string
   * stored on triggerForm (which the API persists).
   */
  private readonly dateDisplayControls = new Map<
    'start' | 'end',
    FormControl<DateTime | null>
  >();

  public getDateDisplayControl(
    which: 'start' | 'end'
  ): FormControl<DateTime | null> {
    let control = this.dateDisplayControls.get(which);
    const sourceControl =
      which === 'start' ? this.startControl : this.endControl;
    const parsedDt = this.parseDate(sourceControl?.value);

    if (!control) {
      control = new FormControl<DateTime | null>(parsedDt);
      this.dateDisplayControls.set(which, control);
      control.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((dt) => {
          // TODO(l10n PD-127900 follow-up): hardcoded US format. Both display
          // and persistence formats need to be revisited once the BusinessRules
          // API contract is confirmed.
          const formatted = dt ? dt.toFormat('MM/dd/yyyy') : null;
          sourceControl?.setValue(formatted ?? '');
        });
    } else if (!this.sameDateTime(control.value, parsedDt)) {
      control.setValue(parsedDt, { emitEvent: false });
    }
    return control;
  }

  private sameDateTime(a: DateTime | null, b: DateTime | null): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.equals(b);
  }

  private parseDate(raw: unknown): DateTime | null {
    if (!raw) return null;
    if (raw instanceof DateTime) return raw.isValid ? raw : null;
    if (raw instanceof Date) {
      const dt = DateTime.fromJSDate(raw);
      return dt.isValid ? dt : null;
    }
    if (typeof raw === 'string') {
      // TODO(l10n PD-127900 follow-up): hardcoded US parse format — paired
      // with the persistence site above.
      const fromFormat = DateTime.fromFormat(raw, 'MM/dd/yyyy');
      if (fromFormat.isValid) return fromFormat;
      const fromIso = DateTime.fromISO(raw);
      return fromIso.isValid ? fromIso : null;
    }
    return null;
  }

  public getAttributeType(attributeName: string): string {
    const attribute = this.attributes()?.find(
      (x) => x.attributeName === attributeName
    );
    return attribute?.attributeDataType ?? AttributeDataType.String;
  }

  public onSave(): void {
    this.isInvalid.set(!this.validateTrigger());
    if (this.isInvalid()) {
      this.handleErrors();
      return;
    }

    const { attribute, start, end, isAnyStart, isAnyEnd } =
      this.triggerForm()!.value;
    const triggerVal = this.trigger();
    this.saveEmitter.emit({
      ...triggerVal,
      parameters: {
        ...triggerVal.parameters,
        AttributeName: attribute,
        // Wildcard means the server should treat the value as "any" —
        // send an empty string regardless of what the control holds.
        FromValue: isAnyStart ? '' : start,
        ToValue: isAnyEnd ? '' : end,
        IsAnyFromValue: !!isAnyStart,
        IsAnyToValue: !!isAnyEnd,
      },
    });
  }

  public onCancel(): void {
    this.cancelEmitter.emit();
  }

  public onBack(): void {
    this.backEmitter.emit();
  }

  public handleErrors(): void {
    setTimeout(() => {
      const form = this.triggerForm()!;
      const firstInvalidControl = Object.keys(form.controls).find(
        (controlName) =>
          controlName !== 'isAnyStart' &&
          controlName !== 'isAnyEnd' &&
          form.get(controlName)?.invalid
      );

      if (firstInvalidControl) {
        if (firstInvalidControl === 'attribute') {
          this.attributesField?.nativeElement
            .querySelector<HTMLElement>('input, button')
            ?.focus();
        } else if (firstInvalidControl === 'start') {
          if (this.datePickers?.first) {
            this.datePickers.first.calendarButtonRef.nativeElement.focus();
          } else if (this.selectComponents?.first) {
            this.focusSelectButton(this.selectComponents.first);
          } else {
            const startingValueElement = this.inputElements.find(
              (element) =>
                element.textInput?.nativeElement.id === 'startingAttributeValue'
            );
            startingValueElement?.textInput.nativeElement.focus();
          }
        } else if (firstInvalidControl === 'end') {
          if (this.datePickers.last) {
            this.datePickers.last.calendarButtonRef.nativeElement.focus();
          } else if (this.selectComponents?.last) {
            this.focusSelectButton(this.selectComponents.last);
          } else {
            const endingAttributeValue = this.inputElements.find(
              (element) =>
                element.textInput?.nativeElement.id === 'endingAttributeValue'
            );
            endingAttributeValue?.textInput.nativeElement.focus();
          }
        }
      }
    });
  }

  private validateTrigger(): boolean {
    const form = this.triggerForm()!;
    form.markAllAsTouched();
    return form.valid;
  }

  /** ApolloDropdownComponent's button ref is private, so reach in via the host. */
  private focusSelectButton(host: ElementRef<HTMLElement>): void {
    host.nativeElement.querySelector<HTMLButtonElement>('button')?.focus();
  }
}
